import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { WorkerType, WorkerTypeListFilters, WorkerTypesResponse } from '../models/worker-types.model';

@Injectable({ providedIn: 'root' })
export class WorkerTypesService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  /**
   * Fetch paginated worker types
   */
  getWorkerTypes(
    pageNumber: number = 1,
    pageSize: number = 25,
    orderBy: string = 'name',
    filters?: WorkerTypeListFilters,
  ): Observable<WorkerTypesResponse | null> {
    let params = new HttpParams()
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(pageSize))
      .set('orderBy', orderBy);

    const searchTerm = String(filters?.searchTerm ?? '').trim();
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    return this.http
      .get<WorkerTypesResponse>(`${this.baseUrl}/api/worker-types`, { params })
      .pipe(
        catchError((err) => {
          console.error('Error fetching worker types:', err);
          return of(null);
        }),
      );
  }

  /**
   * Get worker type by ID
   */
  getWorkerTypeById(id: number): Observable<WorkerType | null> {
    return this.http
      .get<{ status: string; data: WorkerType }>(`${this.baseUrl}/api/worker-types/${id}`)
      .pipe(
        map((res) => res.data),
        catchError((err) => {
          console.error('Error fetching worker type by ID:', err);
          return of(null);
        }),
      );
  }

  /**
   * Create worker type
   */
  createWorkerType(payload: Partial<WorkerType>): Observable<boolean> {
    return this.http
      .post<{ status: string }>(`${this.baseUrl}/api/worker-types`, payload)
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error('Error creating worker type:', err);
          return of(false);
        }),
      );
  }

  /**
   * Update worker type
   */
  updateWorkerType(id: number, payload: Partial<WorkerType>): Observable<boolean> {
    return this.http
      .patch<{ status: string }>(`${this.baseUrl}/api/worker-types/${id}`, payload)
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error('Error updating worker type:', err);
          return of(false);
        }),
      );
  }

  /**
   * Delete worker type
   */
  deleteWorkerType(id: number): Observable<boolean> {
    return this.http
      .delete<{ status: string }>(`${this.baseUrl}/api/worker-types/${id}`)
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error('Error deleting worker type:', err);
          return of(false);
        }),
      );
  }
}